import type { TaskChatAuthorType } from '@frontend/api-client';
import {
  getCommentProps,
  getTaskChatMessageAuthorInfo,
  groupWhispers,
  isClientResponseAuthorType,
} from '@frontend/api-client';
import type { LocalFile, TaskComment } from '@frontend/api-types';
import { match, normalizeForSearch } from '@frontend/duck-tape';
import type { CreateTaskCommentFormSchema } from '@frontend/forms';
import { useCallback, useEffect, useFeatureIsOn, useMemo, useRef } from '@frontend/react';
import { useConversationSearch, useTaskChatSocket } from '@frontend/react-domain/tasks';
import {
  FlatList,
  LoadingSpinnerPanel,
  YStack,
  isMobile,
  useScrollView,
  type FlatListProps,
} from '@frontend/web-react';
import { WebTypedFormData, safeCss } from '@frontend/web-utils';
import {
  GoogleMapWidget,
  TaskChatBrainstormContent,
  TaskChatMessageBubble,
  TaskChatSearchBar,
  TaskChatSecureNoteCard,
  TaskChatSingleChoiceOptions,
  TaskChatTime,
  TaskChatTrace,
  TaskChatWhisper,
  WhisperGroup,
} from '@frontend/web/components';
import { TaskChatOptionSheetCarousel } from '@frontend/web/components/DomainSpecific/Tasks/Chat/TaskChatOptionSheetCarousel';
import {
  useCreateCommentMutation,
  useInvalidateTaskCaches,
  useListCommentsQuery,
  useListTasksQuery,
  useRetrieveTaskQuery,
  useUpdateTaskMutation,
} from '@frontend/web/hooks';
import { Route } from '@frontend/web/routes/app/task/$taskId';
import { disconnectSocket, getSocket, initializeSocket } from '@frontend/web/utils';
import { TaskChatFooterContent } from './TaskChatFooterContent';

type TaskDetailChatTabProps = {
  commentContentFormat?: 'html' | 'markdown';
  isClientSubscribed?: boolean;
  isSearchOpen?: boolean;
  onCloseSearch?: () => void;
};

const SEARCH_HIGHLIGHT_NAME = 'task-chat-search-match';

const collectTextNodes = (container: Element): globalThis.Text[] => {
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  const nodes: globalThis.Text[] = [];
  // eslint-disable-next-line fp/no-let
  let node = walker.nextNode();
  // eslint-disable-next-line fp/no-loops
  while (node) {
    // eslint-disable-next-line fp/no-mutating-methods
    nodes.push(node as globalThis.Text);
    // eslint-disable-next-line fp/no-mutation
    node = walker.nextNode();
  }
  return nodes;
};

const collectRangesForTextNode = (textNode: globalThis.Text, normalizedQuery: string): Range[] => {
  const text = textNode.textContent ?? '';
  const normalizedText = normalizeForSearch(text).toLowerCase();
  // eslint-disable-next-line fp/no-let
  let idx = normalizedText.indexOf(normalizedQuery);

  if (idx === -1) {
    return [];
  }

  const ranges: Range[] = [];
  // eslint-disable-next-line fp/no-loops
  while (idx !== -1) {
    const range = new Range();
    range.setStart(textNode, idx);
    range.setEnd(textNode, idx + normalizedQuery.length);
    // eslint-disable-next-line fp/no-mutating-methods
    ranges.push(range);
    // eslint-disable-next-line fp/no-mutation
    idx = normalizedText.indexOf(normalizedQuery, idx + normalizedQuery.length);
  }

  return ranges;
};

const clearSearchHighlight = () => {
  if (!('highlights' in CSS)) {
    return;
  }

  CSS.highlights.delete(SEARCH_HIGHLIGHT_NAME);
};

export const TaskDetailChatTab = ({
  commentContentFormat = 'markdown',
  isClientSubscribed = true,
  isSearchOpen = false,
  onCloseSearch,
}: TaskDetailChatTabProps) => {
  const { enabled: isGameplanPricingEnabled } = useFeatureIsOn('is_gameplan_pricing_enabled');
  const { taskId } = Route.useParams();
  const { data: task, refetch: refetchTask } = useRetrieveTaskQuery(taskId, { refetchOnMountOrArgChange: true });
  const { scrollRef, scrollToBottom } = useScrollView({ onRenderScroll: 'bottom' });
  const {
    data: comments = [],
    isLoading: isLoadingComments,
    isSuccess,
    refetch: refetchComments,
  } = useListCommentsQuery(
    {
      format: commentContentFormat,
      id: taskId,
    },
    { refetchOnMountOrArgChange: true },
  );
  const [updateTask] = useUpdateTaskMutation();
  const [createComment] = useCreateCommentMutation();
  const { refetch: refetchTasks } = useListTasksQuery();
  const invalidateTaskCaches = useInvalidateTaskCaches();

  const { dispatchSocketEvent, isGameplanGenerating, isThinking, thinkingTrace } = useTaskChatSocket({
    onInvalidateCache: invalidateTaskCaches,
    refetch: () => {
      refetchComments();
      refetchTask();
      refetchTasks();
    },
    socketClient: { disconnectSocket, getSocket, initializeSocket },
    taskId,
  });

  const { isUnread } = task ?? {};

  useEffect(() => {
    if (isSuccess && isUnread) {
      updateTask({ data: { isUnread: false }, id: taskId });
    }
  }, [isSuccess, isUnread]);

  const containerRef = useRef<HTMLDivElement>(null);

  const {
    clearSearch,
    currentMatch,
    currentMatchIndex,
    goToNext,
    goToPrev,
    inputQuery,
    searchQuery,
    setSearchQuery,
    totalMatches,
  } = useConversationSearch(comments);

  const handleCloseSearch = useCallback(() => {
    clearSearch();
    onCloseSearch?.();
  }, [clearSearch, onCloseSearch]);

  useEffect(() => {
    scrollToBottom();
  }, [comments, scrollToBottom]);

  useEffect(() => {
    if (!currentMatch?.commentId || !containerRef.current) return;
    const element = containerRef.current.querySelector(`[data-comment-id="${currentMatch.commentId}"]`);
    element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [currentMatch?.commentId, currentMatchIndex]);

  useEffect(() => {
    clearSearchHighlight();

    if (!containerRef.current || !('highlights' in CSS) || typeof Highlight === 'undefined') {
      return;
    }

    const query = searchQuery.trim();
    if (!query) {
      return;
    }

    const normalizedQuery = normalizeForSearch(query).toLowerCase();
    const searchableContainers = Array.from(
      containerRef.current.querySelectorAll('[data-task-chat-search-content="true"]'),
    );

    const ranges = searchableContainers.flatMap((container) =>
      collectTextNodes(container).flatMap((textNode) => collectRangesForTextNode(textNode, normalizedQuery)),
    );

    if (ranges.length) {
      CSS.highlights.set(SEARCH_HIGHLIGHT_NAME, new Highlight(...ranges));
    }

    return () => {
      clearSearchHighlight();
    };
  }, [searchQuery, comments]);

  const commentsWithDescription = useMemo(
    () => (task ? (isGameplanPricingEnabled ? [...comments] : groupWhispers([...comments])) : []),
    [task, isGameplanPricingEnabled, comments],
  );

  const reversedComments = useMemo(
    () => commentsWithDescription.map((_, i, arr) => arr[arr.length - 1 - i]!),
    [commentsWithDescription],
  );

  const hasPendingGameplan = comments?.some((comment) => comment.complexityAgreement?.status === 'pending');

  // When a pending gameplan appears, generation is complete
  useEffect(() => {
    if (hasPendingGameplan && isGameplanGenerating) {
      dispatchSocketEvent({ type: 'gameplan_generation_completed' });
    }
  }, [dispatchSocketEvent, hasPendingGameplan, isGameplanGenerating]);

  const onCommentCreated = useCallback(() => {
    scrollToBottom();
  }, [scrollToBottom]);

  const submitComment = useCallback(
    async (body: string, attachments?: LocalFile[]) => {
      const typedForm = new WebTypedFormData<CreateTaskCommentFormSchema>();
      typedForm.append('body', body);
      if (attachments?.length) typedForm.appendLocalFiles('attachments', attachments);
      return createComment({ formData: typedForm, taskId }).unwrap();
    },
    [createComment, taskId],
  );

  const renderLoadingState = useCallback(() => {
    if (comments.length > 0 && (isThinking || thinkingTrace)) return <TaskChatTrace thinkingTrace={thinkingTrace} />;
    return null;
  }, [comments.length, isThinking, thinkingTrace]);

  const renderItem = useCallback<FlatListProps<TaskComment | TaskComment[]>['renderItem']>(
    ({ index, item: currentComment }) => {
      if (Array.isArray(currentComment)) {
        if (isGameplanPricingEnabled) return null;
        // Render WhisperGroup for grouped whispers
        return (
          <YStack>
            {index === reversedComments.length - 1 && currentComment[0]?.createdAt ? (
              <TaskChatTime time={currentComment[0]?.createdAt} />
            ) : null}
            <WhisperGroup comments={currentComment} contentType={commentContentFormat} taskId={taskId} />
          </YStack>
        );
      }
      const {
        attachments,
        author,
        body,
        createdAt,
        mapLocations,
        questions,
        secureNote,
        sheets,
        singleChoiceOptions,
        whisper,
      } = currentComment;
      // It's a bit confusing, but because we're rendering the list in reverse,
      // we need to get the previous comment from the next index
      const previousComment = index ? reversedComments[index + 1] : undefined;
      const previousCommentFlat = Array.isArray(previousComment)
        ? previousComment[previousComment.length - 1]
        : previousComment;

      const { isAuthorVisible, isTimeVisible } = getCommentProps({
        currentComment,
        previousComment: previousCommentFlat,
      });

      const hasClientResponseAfter = reversedComments
        .slice(0, index)
        .some((c) => !Array.isArray(c) && isClientResponseAuthorType(c.author?.type));

      const content = (() => {
        if (secureNote) {
          const { id, isActive, title } = secureNote;
          return (
            <TaskChatSecureNoteCard
              className="ml-xl"
              isActive={isActive}
              secureNoteId={id}
              taskId={taskId}
              title={title}
            />
          );
        }

        const singleChoiceOptionsComponent = singleChoiceOptions?.length ? (
          <TaskChatSingleChoiceOptions
            analyticsTopic="taskGameplanSuggestions"
            commentId={currentComment.id}
            suggestions={singleChoiceOptions}
            taskId={taskId}
          />
        ) : null;

        const hasBrainstormContent =
          !!questions && (questions.type === 'brainstorm_options' || questions.type === 'clarifying_questions');

        const brainstormComponent = hasBrainstormContent ? (
          <TaskChatBrainstormContent
            hasClientResponseAfter={hasClientResponseAfter}
            onSubmitComment={submitComment}
            questions={questions}
          />
        ) : null;

        const optionSheetCarousel = sheets?.[0] ? (
          <TaskChatOptionSheetCarousel onSendMessage={submitComment} optionSheetId={sheets[0].id} taskId={taskId} />
        ) : null;

        const mapWidget = mapLocations?.locations.length ? (
          <GoogleMapWidget locations={mapLocations.locations} />
        ) : null;

        return match(author)
          .with({ type: 'system_bot' }, () =>
            isGameplanPricingEnabled ? (
              <YStack gap="sm" pb="sm">
                {whisper ? null : <TaskChatTime time={createdAt} />}
                {brainstormComponent ?? (
                  <TaskChatWhisper
                    body={body}
                    commentId={currentComment.id}
                    contentType={commentContentFormat}
                    createdAt={createdAt}
                    taskId={taskId}
                    whisper={whisper}
                  />
                )}
                {optionSheetCarousel}
                {singleChoiceOptionsComponent}
                {mapWidget}
              </YStack>
            ) : (
              <YStack gap="sm">
                <TaskChatWhisper
                  body={body}
                  commentId={currentComment.id}
                  contentType={commentContentFormat}
                  createdAt={createdAt}
                  taskId={taskId}
                  whisper={whisper}
                />
                {optionSheetCarousel}
                {mapWidget}
              </YStack>
            ),
          )
          .otherwise((author) => (
            <YStack gap="sm">
              {!brainstormComponent && (
                <TaskChatMessageBubble
                  {...currentComment}
                  attachments={attachments}
                  authorInfo={getTaskChatMessageAuthorInfo({ ...author, type: author.type as TaskChatAuthorType })}
                  body={body}
                  contentType={commentContentFormat}
                  isAuthorVisible={isAuthorVisible}
                  onAgreementActionComplete={async () => {
                    await refetchComments();
                    await refetchTask();
                    refetchTasks();
                  }}
                  taskId={taskId}
                />
              )}
              {optionSheetCarousel}
              {brainstormComponent}
              {singleChoiceOptionsComponent}
              {mapWidget}
            </YStack>
          ));
      })();

      return (
        <YStack data-comment-id={currentComment.id} gapY="md">
          {isTimeVisible && !isGameplanPricingEnabled && !currentComment.whisper ? (
            <TaskChatTime time={createdAt} />
          ) : null}
          {content}
          {index === 0 ? renderLoadingState() : null}
        </YStack>
      );
    },
    [
      commentContentFormat,
      searchQuery,
      isGameplanPricingEnabled,
      reversedComments,
      refetchComments,
      refetchTask,
      refetchTasks,
      renderLoadingState,
      submitComment,
      taskId,
    ],
  );

  const chatFooterContent = (
    <TaskChatFooterContent
      hasPendingGameplan={hasPendingGameplan}
      isClientSubscribed={isClientSubscribed}
      isGameplanGenerating={isGameplanGenerating}
      onCommentCreated={onCommentCreated}
      submitComment={submitComment}
      task={task}
    />
  );

  return (
    <div style={{ display: 'contents' }}>
      {isSearchOpen ? (
        <TaskChatSearchBar
          currentMatchIndex={currentMatchIndex}
          onClose={handleCloseSearch}
          onGoToNext={goToNext}
          onGoToPrev={goToPrev}
          onSearchChange={setSearchQuery}
          searchQuery={inputQuery}
          totalMatches={totalMatches}
        />
      ) : null}
      <div ref={containerRef} style={{ display: 'contents' }}>
        <FlatList
          ListFooterComponent={
            <YStack className="bottom-none bg-trueWhite sticky z-10">{!isMobile ? chatFooterContent : null}</YStack>
          }
          ListLoadingComponent={<LoadingSpinnerPanel fullScreen />}
          className="px-md pb-md flex-1"
          contentContainerClassName={safeCss(
            isMobile
              ? 'pt-md pb-[180px] gap-y-md max-w-screen-md self-center w-full justify-end flex-1'
              : 'pt-md pb-xxl gap-y-md max-w-screen-md self-center w-full justify-end flex-1',
          )}
          data={reversedComments}
          inverted
          isLoading={isLoadingComments}
          ref={scrollRef}
          renderItem={renderItem}
        />
        {isMobile ? (
          <YStack className="absolute bg-trueWhite z-10 w-full px-md pb-md bottom-sm">{chatFooterContent}</YStack>
        ) : null}
      </div>
    </div>
  );
};
